import { openDrawer } from "@/src/components/dialogs";
import apiFetch from "@/src/utils/apiFetch";
import { debounce, getServiceId, uid } from "@/src/utils/utils";
import { Scanner } from "@yudiel/react-qr-scanner";
import moment from "moment";
import { useEffect, useState } from "react";

export type BarcodeObject = {
  rawBarcode: string;
  productBarcode?: string | null;
  lotBarcode?: string | null;
  expiryDate?: string | null;
  manufacturingDate?: string | null;
  initialQuantity?: number | null;
  firstName?: string | null;
  lastName?: string | null;
  birth?: string | null;
  ref?: string | null;
  barcodeCategory?: "productAndLot" | "productOnly" | "lotOnly" | "patient";
};

type OnScanFunction = (obj: BarcodeObject) => void;

export async function openBarcodeScanner({
  onScan,
}: {
  onScan: OnScanFunction;
}) {
  let id = uid();
  await openDrawer({
    id: `barcodeScanner-${id}`,
    title: "Scanner de code barre",
    content: <BarcodeReader onScan={onScan} allowCamera={true} />,
  });
}

export async function getBarcodeAnalysis(barcodeObj: BarcodeObject) {
  let { productBarcode, lotBarcode } = barcodeObj;
  if (!productBarcode && !lotBarcode) return null;
  return await apiFetch("products", "analyseBarcode", {
    serviceId: getServiceId(),
    productBarcode,
    lotBarcode,
  });
}

export function BarcodeReader({
  onScan,
  allowCamera,
}: {
  onScan: OnScanFunction;
  allowCamera: boolean;
}) {
  const [barcode, setBarcode] = useState("");

  useEffect(() => {
    const handleKeyPress = (e: KeyboardEvent) => {
      let key = e.key;

      setBarcode((barcode) => barcode + key);
      debounce(
        "stopScanning",
        () => {
          if (barcode.length > 5) onScan(parseBarcode(barcode + key));
          setBarcode("");
        },
        100
      );
      return;
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (barcode.length > 3 && e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
      }
      if (barcode.length > 3 && e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (barcode.length > 3 && e.key === "Tab") {
        e.preventDefault();
        e.stopPropagation();
      }
      if (barcode.length > 3 && e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("keypress", handleKeyPress);
    return () => {
      window.removeEventListener("keypress", handleKeyPress);
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [barcode]);

  return (
    <div className="c1 gap10">
      {allowCamera && (
        <Scanner
          // onScan={(result) => setBarcode(result[0].rawValue)}
          onScan={(result) => {
            let rawValue = result[0].rawValue;
            setBarcode(rawValue);
            onScan(parseBarcode(rawValue));
          }}
          allowMultiple={true}
          components={{
            tracker: (props) => {
              console.log(props);
              return <div>Tracker</div>;
            },
          }}
          formats={[
            "aztec",
            "codabar",
            "code_128",
            "code_39",
            "ean_13",
            "ean_8",
            "databar_expanded",
            "databar_limited",
            "dx_film_edge",
            "itf",
            "ean_13",
            "ean_8",
            "linear_codes",
            "code_93",
            "maxi_code",
            "matrix_codes",
            "qr_code",
            "micro_qr_code",
            "pdf417",
            "rm_qr_code",
            "upc_a",
            "upc_e",
            "unknown",
          ]}
          constraints={{ aspectRatio: 1 }}
        />
      )}
      {/* <div
        onClick={() => {
          // let barcode = "(01)1234567890128(17)250101(10)LOT12345";
          let barcode = "FEDDAG RAFIK 25/07/93";
          setBarcode(barcode);
          let parsed = parseBarcode(barcode);
          onScan(parsed);
          console.log(parsed);
        }}
      >
        Here: {barcode}
      </div> */}
      {/* <div>Barcode: {barcode}</div>
      {barcode && (
        <div style={{ maxWidth: "100%", overflow: "hidden" }}>
          Parsed: {JSON.stringify(parseBarcode(barcode))}
        </div>
      )} */}
    </div>
  );
}

function isPatientBarcode(rawBarcode: string) {
  let slashCount = 0;
  let spaceCount = 0;
  for (let i = 0; i < rawBarcode.length; i++) {
    if (rawBarcode[i] === "/") slashCount++;
    if (rawBarcode[i] === " ") spaceCount++;
  }
  return slashCount === 2 && spaceCount > 0;
}

function parsePatientBarcode(rawBarcode: string) {
  let rawParts = rawBarcode.split(" ");
  let parts: string[] = [];
  for (let part of rawParts) {
    if (!part.trim().length) continue;
    parts.push(part);
  }

  let birth = moment(parts[parts.length - 1], "DD/MM/YY").format("YYYY-MM-DD");

  if (moment(birth).isAfter(moment()))
    birth = moment(birth).subtract(100, "years").format("YYYY-MM-DD");

  let firstName = parts[parts.length - 2];
  let lastName = parts.slice(0, parts.length - 2).join(" ");
  return {
    productBarcode: null,
    lotBarcode: null,
    expiryDate: null,
    manufacturingDate: null,
    initialQuantity: null,
    ref: null,
    barcodeCategory: "patient",
    firstName,
    lastName,
    birth,
    rawBarcode,
  } as BarcodeObject;
}

export function parseBarcode(rawBarcode: string): BarcodeObject {
  rawBarcode = rawBarcode.trim();
  if (isPatientBarcode(rawBarcode)) return parsePatientBarcode(rawBarcode);
  let data: {
    productBarcode?: string | null;
    lotBarcode?: string | null;
    expiryDate?: string | null;
    manufacturingDate?: string | null;
    initialQuantity?: number | null;
    ref?: string | null;
    barcodeCategory?: "productAndLot" | "productOnly" | "lotOnly";
  } = {};

  const type = getBarcodeType(rawBarcode);

  if (type === "GS1") data = parseGS1Barcode(rawBarcode);
  if (type === "HIBC") data = parseHIBCBarcode(rawBarcode);
  if (type === "NDC") data = parseNDCBarcode(rawBarcode);
  if (type === "isbt128") data = parseISBT128Barcode(rawBarcode);
  if (type === "pharmacode") data = parsePharmacodeBarcode(rawBarcode);
  if (type === "upc") data = parseUPCBarcode(rawBarcode);
  if (type === "ean_13") data = parseEANBarcode(rawBarcode);
  if (type === "ean_8") data = parseEANBarcode(rawBarcode);
  if (type === "code_39") data = parseCode39Barcode(rawBarcode, "|");
  if (type === "code_128") data = parseCode128Barcode(rawBarcode, "|");
  if (type === "data_matrix") data = parseDataMatrixBarcode(rawBarcode);
  if (type === "qr_code") data = parseQRCode(rawBarcode);
  if (type === "Unknown") {
    // openToast("error", `Format de code barre inconnu: ${rawBarcode}`);
  }

  function getCategory() {
    if (data.productBarcode && data.lotBarcode) return "productAndLot";
    if (data.productBarcode) return "productOnly";
    if (data.lotBarcode) return "lotOnly";
    return "lotOnly";
  }

  data.barcodeCategory = getCategory();

  return {
    rawBarcode,
    ...data,
  };
}

function getBarcodeType(rawBarcode: string) {
  if (isGS1Barcode(rawBarcode)) return "GS1";
  if (isHIBCBarcode(rawBarcode)) return "HIBC";
  if (isNDCBarcode(rawBarcode)) return "NDC";
  if (isISBT128Barcode(rawBarcode)) return "isbt128";
  if (isPharmacodeBarcode(rawBarcode)) return "pharmacode";
  if (isUPCBarcode(rawBarcode)) return "upc";
  if (isEAN13Barcode(rawBarcode)) return "ean_13";
  if (isEAN8Barcode(rawBarcode)) return "ean_8";
  if (isCode39Barcode(rawBarcode)) return "code_39";
  if (isCode128Barcode(rawBarcode)) return "code_128";
  if (isDataMatrixBarcode(rawBarcode)) return "data_matrix";
  if (isQRCode(rawBarcode)) return "qr_code";
  return "Unknown";
}

// Function to check if it's a GS1 barcode
function isGS1Barcode(rawBarcode: string): boolean {
  return rawBarcode.startsWith("(");
}

// Function to check if it's a HIBC barcode
function isHIBCBarcode(rawBarcode: string): boolean {
  return rawBarcode.startsWith("+");
}

// Function to check if it's an NDC barcode
function isNDCBarcode(rawBarcode: string): boolean {
  const cleanedBarcode = rawBarcode.replace(/-/g, "");
  return /^\d{10}$/.test(cleanedBarcode) || /^\d{11}$/.test(cleanedBarcode);
}

// Function to check if it's an ISBT 128 barcode
function isISBT128Barcode(rawBarcode: string): boolean {
  return rawBarcode.startsWith("=");
}

// Function to check if it's a Pharmacode barcode
function isPharmacodeBarcode(rawBarcode: string): boolean {
  return (
    /^\d{1,6}$/.test(rawBarcode) &&
    parseInt(rawBarcode, 10) >= 3 &&
    parseInt(rawBarcode, 10) <= 131070
  );
}

// Function to check if it's a UPC barcode
function isUPCBarcode(rawBarcode: string): boolean {
  const cleanedBarcode = rawBarcode.replace(/\s+/g, "");
  return /^\d{12}$/.test(cleanedBarcode);
}

// Function to check if it's an EAN barcode
function isEAN13Barcode(rawBarcode: string): boolean {
  const cleanedBarcode = rawBarcode.replace(/\s+/g, "");
  return /^\d{8}$/.test(cleanedBarcode) || /^\d{13}$/.test(cleanedBarcode);
}

function isEAN8Barcode(rawBarcode: string): boolean {
  const cleanedBarcode = rawBarcode.replace(/\s+/g, "");
  return /^\d{8}$/.test(cleanedBarcode);
}

// Function to check if it's a Code 39 barcode
function isCode39Barcode(rawBarcode: string): boolean {
  const cleanedBarcode = rawBarcode.trim();
  return cleanedBarcode.startsWith("*") && cleanedBarcode.endsWith("*");
}

// Function to check if it's a Code 128 barcode
function isCode128Barcode(rawBarcode: string): boolean {
  return /^[\x20-\x7E]+$/.test(rawBarcode) && rawBarcode.includes("|");
}

// Function to check if it's a Data Matrix barcode
function isDataMatrixBarcode(rawBarcode: string): boolean {
  return rawBarcode.startsWith("DM:");
}

// Function to check if it's a QR Code
function isQRCode(rawBarcode: string): boolean {
  // Assuming QR Codes include 'QR:' prefix for identification
  return rawBarcode.startsWith("QR:");
}

/* Barcode Parsing Functions */

// Function to parse GS1 barcodes
function parseGS1Barcode(rawBarcode: string) {
  const aiRegex = /\((\d{2})\)([^\(]+)/g;
  let match: RegExpExecArray | null;

  // Map to store AI codes and their corresponding values
  const aiData: { [key: string]: string } = {};

  // Extract AI segments from the barcode
  while ((match = aiRegex.exec(rawBarcode)) !== null) {
    const aiCode = match[1];
    const aiValue = match[2];
    aiData[aiCode] = aiValue.trim();
  }

  // Initialize variables
  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let manufacturingDate: string | null = null;
  let initialQuantity: number | null = null;
  let ref: string | null = null;

  // Parse the extracted AI data
  for (const aiCode in aiData) {
    const value = aiData[aiCode];
    switch (aiCode) {
      case "01": // GTIN (Product Barcode)
        productBarcode = value;
        break;
      case "10": // Lot/Batch Number
        lotBarcode = value;
        break;
      case "17": // Expiry Date (YYMMDD)
        expiryDate = parseDate(value);
        break;
      case "11": // Production Date (YYMMDD)
        manufacturingDate = parseDate(value);
        break;
      case "30": // Quantity
        initialQuantity = parseInt(value, 10);
        break;
      case "21": // Serial Number (used here as ref)
        ref = value;
        break;
      default:
        // Handle other AIs if necessary
        break;
    }
  }

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    manufacturingDate,
    initialQuantity,
    ref,
  };
}

// Function to parse HIBC barcodes
function parseHIBCBarcode(rawBarcode: string) {
  // Remove any leading '+' and split primary and secondary data
  const parts = rawBarcode.substring(1).split("/");
  const primaryData = parts[0];
  const secondaryData = parts.length > 1 ? parts[1] : null;

  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let ref: string | null = null;

  // Parse Primary Data Structure
  if (primaryData) {
    // Exclude check character
    const primaryWithoutCheckChar = primaryData.slice(0, -1);
    // Extract LIC (first 4 characters)
    const lic = primaryWithoutCheckChar.substring(0, 4);
    // Extract Product or Catalog Number (next up to 18 characters)
    const productAndUOM = primaryWithoutCheckChar.substring(4);
    // The last character is Unit of Measure
    const uom = productAndUOM.slice(-1);
    const productCode = productAndUOM.slice(0, -1);
    // Combine LIC and Product Code to form productBarcode
    productBarcode = lic + productCode;
    // Use UOM if needed
  }

  // Parse Secondary Data Structure
  if (secondaryData && secondaryData.startsWith("+")) {
    // Exclude check character
    const secondaryWithoutCheckChar = secondaryData.slice(1, -1);
    // Check if it starts with '$$' indicating extended data
    if (secondaryWithoutCheckChar.startsWith("$$")) {
      const data = secondaryWithoutCheckChar.substring(2);
      // Expiry Date (first 4 or 6 digits)
      const dateMatch = data.match(/^(\d{4}|\d{6})/);
      if (dateMatch) {
        const dateStr = dateMatch[0];
        expiryDate = parseDate(dateStr);
        // The rest is Lot/Batch Number
        lotBarcode = data.substring(dateStr.length);
      }
    }
  }

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    ref,
  };
}

// Function to parse NDC barcodes
function parseNDCBarcode(rawBarcode: string) {
  const cleanedBarcode = rawBarcode.replace(/-/g, "");
  let productBarcode: string | null = null;

  if (/^\d{10}$/.test(cleanedBarcode) || /^\d{11}$/.test(cleanedBarcode)) {
    productBarcode = cleanedBarcode;
  }

  return {
    productBarcode,
  };
}

// Function to parse ISBT 128 barcodes
function parseISBT128Barcode(rawBarcode: string) {
  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let manufacturingDate: string | null = null;
  let ref: string | null = null;

  // Remove leading '='
  const cleanedBarcode = rawBarcode.replace(/^=/, "").trim();
  // Split the barcode based on '|'
  const parts = cleanedBarcode.split("|");

  if (parts.length >= 1) {
    productBarcode = parts[0]; // Donation Identification Number
  }
  if (parts.length >= 2) {
    lotBarcode = parts[1]; // Product Code
  }
  if (parts.length >= 3) {
    expiryDate = parseDate(parts[2]); // Expiry Date
  }
  if (parts.length >= 4) {
    manufacturingDate = parseDate(parts[3]); // Collection Date
  }
  if (parts.length >= 5) {
    ref = parts[4]; // Additional Info
  }

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    manufacturingDate,
    ref,
  };
}

// Function to parse Pharmacode barcodes
function parsePharmacodeBarcode(rawBarcode: string) {
  const pharmacodeValue = parseInt(rawBarcode, 10);
  let productBarcode: string | null = null;

  if (
    !isNaN(pharmacodeValue) &&
    pharmacodeValue >= 3 &&
    pharmacodeValue <= 131070
  ) {
    productBarcode = rawBarcode;
  }

  return {
    productBarcode,
  };
}

// Function to parse UPC barcodes
function parseUPCBarcode(rawBarcode: string) {
  const cleanedBarcode = rawBarcode.replace(/\s+/g, "");
  let productBarcode: string | null = null;

  if (/^\d{12}$/.test(cleanedBarcode)) {
    productBarcode = cleanedBarcode;
  }

  return {
    productBarcode,
  };
}

// Function to parse EAN barcodes
function parseEANBarcode(rawBarcode: string) {
  const cleanedBarcode = rawBarcode.replace(/\s+/g, "");
  let productBarcode: string | null = null;

  if (/^\d{8}$/.test(cleanedBarcode) || /^\d{13}$/.test(cleanedBarcode)) {
    productBarcode = cleanedBarcode;
  }

  return {
    productBarcode,
  };
}

// Function to parse Code 39 barcodes
function parseCode39Barcode(rawBarcode: string, delimiter: string) {
  // Remove start/stop characters '*'
  const cleanedBarcode = rawBarcode.replace(/^\*|\*$/g, "").trim();
  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let ref: string | null = null;

  const parts = cleanedBarcode.split(delimiter);
  if (parts.length >= 1) {
    productBarcode = parts[0];
  }
  if (parts.length >= 2) {
    lotBarcode = parts[1];
  }
  if (parts.length >= 3) {
    expiryDate = parseDate(parts[2]);
  }
  if (parts.length >= 4) {
    ref = parts[3];
  }

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    ref,
  };
}

// Function to parse Code 128 barcodes
function parseCode128Barcode(rawBarcode: string, delimiter: string) {
  const cleanedBarcode = rawBarcode.trim();
  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let ref: string | null = null;

  const parts = cleanedBarcode.split(delimiter);
  if (parts.length >= 1) {
    productBarcode = parts[0];
  }
  if (parts.length >= 2) {
    lotBarcode = parts[1];
  }
  if (parts.length >= 3) {
    expiryDate = parseDate(parts[2]);
  }
  if (parts.length >= 4) {
    ref = parts[3];
  }

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    ref,
  };
}

// Function to parse Data Matrix barcodes
function parseDataMatrixBarcode(rawBarcode: string) {
  const cleanedBarcode = rawBarcode.replace(/^DM:/, "").trim();
  const gs1Barcode = insertGS1Parentheses(cleanedBarcode);
  return parseGS1Barcode(gs1Barcode);
}

function parseQRCode(rawBarcode: string) {
  const cleanedBarcode = rawBarcode.replace(/^QR:/, "").trim();
  let productBarcode: string | null = null;
  let lotBarcode: string | null = null;
  let expiryDate: string | null = null;
  let manufacturingDate: string | null = null;
  let initialQuantity: number | null = null;
  let ref: string | null = null;

  // Assume data is in URL query string format
  const params = new URLSearchParams(cleanedBarcode);
  productBarcode = params.get("productBarcode");
  lotBarcode = params.get("lotBarcode");
  const expiryDateStr = params.get("expiryDate");
  if (expiryDateStr) expiryDate = parseDate(expiryDateStr);
  const manufacturingDateStr = params.get("manufacturingDate");
  if (manufacturingDateStr) manufacturingDate = parseDate(manufacturingDateStr);
  const initialQuantityStr = params.get("initialQuantity");
  if (initialQuantityStr) initialQuantity = parseInt(initialQuantityStr, 10);

  ref = params.get("ref");

  return {
    productBarcode,
    lotBarcode,
    expiryDate,
    manufacturingDate,
    initialQuantity,
    ref,
  };
}

function parseDate(dateStr: string): string | null {
  if (dateStr.length === 4) return parseDateYYMM(dateStr);
  if (dateStr.length === 6) return parseDateYYMMDD(dateStr);
  return null;
}

function parseDateYYMMDD(dateStr: string): string | null {
  if (dateStr.length !== 6) return null;
  const year = "20" + dateStr.substring(0, 2);
  const month = dateStr.substring(2, 4);
  const day = dateStr.substring(4, 6);
  return `${year}-${month}-${day}`;
}

function parseDateYYMM(dateStr: string): string | null {
  if (dateStr.length !== 4) return null;
  const year = "20" + dateStr.substring(0, 2);
  const month = dateStr.substring(2, 4);
  return `${year}-${month}-01`;
}

function insertGS1Parentheses(data: string): string {
  const aiPatterns = [
    { ai: "01", length: 14 },
    { ai: "10", variable: true }, // Lot/Batch number
    { ai: "17", length: 6 }, // Expiry date
    { ai: "11", length: 6 }, // Production date
    { ai: "21", variable: true }, // Serial number
    { ai: "30", variable: true }, // Quantity
    // Add other AIs as needed
  ];

  let index = 0;
  let result = "";
  while (index < data.length) {
    let matched = false;
    for (const aiPattern of aiPatterns) {
      const aiCode = data.substring(index, index + aiPattern.ai.length);
      if (aiCode === aiPattern.ai) {
        result += `(${aiCode})`;
        index += aiPattern.ai.length;
        if (aiPattern.variable) {
          // Read until the next AI or end of data
          const nextAIIndex = data.substring(index).search(/\d{2}/);
          if (nextAIIndex !== -1) {
            result += data.substring(index, index + nextAIIndex);
            index += nextAIIndex;
          } else {
            result += data.substring(index);
            index = data.length;
          }
        } else {
          result += data.substring(index, index + (aiPattern?.length || 0));
          index += aiPattern?.length || 0;
        }
        matched = true;
        break;
      }
    }
    if (!matched) {
      result += data.charAt(index);
      index++;
    }
  }
  return result;
}
